import React, { useState, useEffect, useRef, useCallback } from 'react';
import './index.scss';

const ROWS = 20;
const COLS = 10;
const BLOCK_SIZE = 30;
const SPEEDS = [800, 650, 500, 370, 250, 160, 100, 70, 50, 30];

const SHAPES = [
  // I
  [
    [0, 0, 0, 0],
    [1, 1, 1, 1],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ],
  // J
  [
    [1, 0, 0],
    [1, 1, 1],
    [0, 0, 0],
  ],
  // L
  [
    [0, 0, 1],
    [1, 1, 1],
    [0, 0, 0],
  ],
  // O
  [
    [1, 1],
    [1, 1],
  ],
  // S
  [
    [0, 1, 1],
    [1, 1, 0],
    [0, 0, 0],
  ],
  // T
  [
    [0, 1, 0],
    [1, 1, 1],
    [0, 0, 0],
  ],
  // Z
  [
    [1, 1, 0],
    [0, 1, 1],
    [0, 0, 0],
  ],
];

const COLORS = [
  '#00BCD4', // I
  '#3F51B5', // J
  '#FF9800', // L
  '#FFEB3B', // O
  '#4CAF50', // S
  '#9C27B0', // T
  '#F44336', // Z
];

function randomShape() {
  const idx = Math.floor(Math.random() * SHAPES.length);
  return { shape: SHAPES[idx], color: COLORS[idx], type: idx };
}

function rotate(matrix) {
  return matrix[0].map((_, i) => matrix.map(row => row[i]).reverse());
}

function checkCollision(board, shape, pos) {
  for (let y = 0; y < shape.length; y++) {
    for (let x = 0; x < shape[y].length; x++) {
      if (shape[y][x]) {
        const newY = pos.y + y;
        const newX = pos.x + x;
        if (
          newY < 0 ||
          newY >= ROWS ||
          newX < 0 ||
          newX >= COLS ||
          board[newY][newX]
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

function merge(board, shape, pos, colorIdx) {
  const newBoard = board.map(row => [...row]);
  for (let y = 0; y < shape.length; y++) {
    for (let x = 0; x < shape[y].length; x++) {
      if (shape[y][x]) {
        newBoard[pos.y + y][pos.x + x] = colorIdx + 1;
      }
    }
  }
  return newBoard;
}

function clearLines(board) {
  let lines = 0;
  const newBoard = board.filter(row => {
    if (row.every(cell => cell !== 0)) {
      lines++;
      return false;
    }
    return true;
  });
  while (newBoard.length < ROWS) {
    newBoard.unshift(Array(COLS).fill(0));
  }
  return { newBoard, lines };
}

const Tetris = () => {
  const [board, setBoard] = useState(Array(ROWS).fill(0).map(() => Array(COLS).fill(0)));
  const [current, setCurrent] = useState(randomShape());
  const [pos, setPos] = useState({ x: 3, y: 0 });
  const [next, setNext] = useState(randomShape());
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(0);
  const [lines, setLines] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [paused, setPaused] = useState(false);
  const [bestScore, setBestScore] = useState(0);
  const intervalRef = useRef();
  
  // 使用 useRef 存储当前状态，避免定时器中的闭包问题
  const gameStateRef = useRef({
    gameOver,
    paused,
    board,
    current,
    pos,
    lines,
    score,
    level,
    bestScore
  });
  
  // 更新 ref 中的状态
  useEffect(() => {
    gameStateRef.current = {
      gameOver,
      paused,
      board,
      current,
      pos,
      lines,
      score,
      level,
      bestScore
    };
  });

  // 添加事件监听器
  useEffect(() => {
    const handleGameControl = (event) => {
      const { action, gameType } = event.detail;
      if (gameType !== 'tetris') return;
      
      switch (action) {
        case 'up':
        case 'rotate':
          if (!gameOver && !paused) rotateShape();
          break;
        case 'left':
          if (!gameOver && !paused) move(-1);
          break;
        case 'right':
          if (!gameOver && !paused) move(1);
          break;
        case 'down':
          if (!gameOver && !paused) drop();
          break;
        case 'pause':
          if (!gameOver) setPaused(p => !p);
          break;
        case 'restart':
          restart();
          break;
        default:
          break;
      }
    };

    window.addEventListener('gameControl', handleGameControl);
    return () => window.removeEventListener('gameControl', handleGameControl);
  }, [gameOver, paused]);

  // 下落速度
  const getSpeed = () => SPEEDS[Math.min(level, SPEEDS.length - 1)];

  // 新方块
  const spawn = useCallback(() => {
    setCurrent(next);
    setPos({ x: 3, y: 0 });
    setNext(randomShape());
  }, [next]);

  // 下落逻辑
  const drop = useCallback(() => {
    if (gameOver || paused) return;
    const newPos = { x: pos.x, y: pos.y + 1 };
    if (!checkCollision(board, current.shape, newPos)) {
      setPos(newPos);
    } else {
      // 合并到棋盘
      const merged = merge(board, current.shape, pos, current.type);
      const { newBoard, lines: cleared } = clearLines(merged);
      setBoard(newBoard);
      setScore(s => s + [0, 100, 300, 700, 1500][cleared]);
      setLines(l => l + cleared);
      if (Math.floor((lines + cleared) / 10) > level) {
        setLevel(lv => lv + 1);
      }
      // 判断游戏结束
      if (pos.y === 0) {
        setGameOver(true);
        if (score > bestScore) {
          setBestScore(score);
          localStorage.setItem('tetris_best_score', score.toString());
        }
        return;
      }
      spawn();
    }
  }, [board, current, pos, spawn, gameOver, paused, lines, level, score, bestScore]);

  // 侧移
  const move = useCallback(dir => {
    if (gameOver || paused) return;
    const newPos = { x: pos.x + dir, y: pos.y };
    if (!checkCollision(board, current.shape, newPos)) {
      setPos(newPos);
    }
  }, [board, current, pos, gameOver, paused]);

  // 旋转
  const rotateShape = useCallback(() => {
    if (gameOver || paused) return;
    const rotated = rotate(current.shape);
    if (!checkCollision(board, rotated, pos)) {
      setCurrent({ ...current, shape: rotated });
    }
  }, [board, current, pos, gameOver, paused]);

  // 快速下落
  const hardDrop = useCallback(() => {
    if (gameOver || paused) return;
    let newY = pos.y;
    while (!checkCollision(board, current.shape, { x: pos.x, y: newY + 1 })) {
      newY++;
    }
    setPos({ x: pos.x, y: newY });
  }, [board, current, pos, gameOver, paused]);

  // 键盘事件
  useEffect(() => {
    const handleKey = e => {
      if (gameOver) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          move(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          move(1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          drop();
          break;
        case 'ArrowUp':
          e.preventDefault();
          rotateShape();
          break;
        case ' ': // 空格
          e.preventDefault();
          hardDrop();
          break;
        case 'p':
        case 'P':
          setPaused(p => !p);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [move, drop, rotateShape, hardDrop, gameOver]);

  // 自动下落
  useEffect(() => {
    if (gameOver || paused) return;
    
    const interval = setInterval(() => {
      const state = gameStateRef.current;
      if (!state.gameOver && !state.paused) {
        const newPos = { x: state.pos.x, y: state.pos.y + 1 };
        if (!checkCollision(state.board, state.current.shape, newPos)) {
          setPos(newPos);
        } else {
          // 合并到棋盘
          const merged = merge(state.board, state.current.shape, state.pos, state.current.type);
          const { newBoard, lines: cleared } = clearLines(merged);
          setBoard(newBoard);
          setScore(s => s + [0, 100, 300, 700, 1500][cleared]);
          setLines(l => l + cleared);
          if (Math.floor((state.lines + cleared) / 10) > state.level) {
            setLevel(lv => lv + 1);
          }
          // 判断游戏结束
          if (state.pos.y === 0) {
            setGameOver(true);
            if (state.score > state.bestScore) {
              setBestScore(state.score);
              localStorage.setItem('tetris_best_score', state.score.toString());
            }
            return;
          }
          spawn();
        }
      }
    }, getSpeed());
    
    intervalRef.current = interval;
    return () => clearInterval(interval);
  }, [gameOver, paused, level]);

  // 初始化
  useEffect(() => {
    const best = localStorage.getItem('tetris_best_score');
    if (best) setBestScore(parseInt(best));
  }, []);

  // 重开
  const restart = () => {
    setBoard(Array(ROWS).fill(0).map(() => Array(COLS).fill(0)));
    setCurrent(randomShape());
    setNext(randomShape());
    setPos({ x: 3, y: 0 });
    setScore(0);
    setLevel(0);
    setLines(0);
    setGameOver(false);
    setPaused(false);
  };

  // 渲染棋盘
  const renderBoard = () => {
    const display = board.map(row => [...row]);
    // 叠加当前方块
    for (let y = 0; y < current.shape.length; y++) {
      for (let x = 0; x < current.shape[y].length; x++) {
        if (current.shape[y][x]) {
          const by = pos.y + y;
          const bx = pos.x + x;
          if (by >= 0 && by < ROWS && bx >= 0 && bx < COLS) {
            display[by][bx] = current.type + 1;
          }
        }
      }
    }
    return display.map((row, y) => (
      <div className="tetris-row" key={y}>
        {row.map((cell, x) => (
          <div
            key={x}
            className={cell ? 'tetris-cell filled' : 'tetris-cell'}
            style={{ background: cell ? COLORS[cell - 1] : 'rgba(255,255,255,0.08)' }}
          />
        ))}
      </div>
    ));
  };

  // 渲染下一个方块
  const renderNext = () => {
    return (
      <div className="tetris-next-block">
        {next.shape.map((row, y) => (
          <div className="tetris-row" key={y}>
            {row.map((cell, x) => (
              <div
                key={x}
                className={cell ? 'tetris-cell filled' : 'tetris-cell'}
                style={{ background: cell ? next.color : 'transparent' }}
              />
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="tetris-game">
      <div className="tetris-header">
        <div className="tetris-scores">
          <div className="score-container">
            <div className="score-label">分数</div>
            <div className="score-value">{score}</div>
          </div>
          <div className="score-container">
            <div className="score-label">最高分</div>
            <div className="score-value">{bestScore}</div>
          </div>
          <div className="score-container">
            <div className="score-label">等级</div>
            <div className="score-value">{level + 1}</div>
          </div>
          <div className="score-container">
            <div className="score-label">消除行</div>
            <div className="score-value">{lines}</div>
          </div>
        </div>
      </div>
      {/* 游戏控制按钮区域 */}
      <div className="tetris-controls">
        <div className="control-row">
          <button 
            className="control-btn rotate-btn" 
            onClick={rotateShape}
            disabled={gameOver || paused}
            title="旋转"
          >
            <span className="btn-icon">🔄</span>
          </button>
          <button 
            className="control-btn hard-drop-btn" 
            onClick={hardDrop}
            disabled={gameOver || paused}
            title="快速下落"
          >
            <span className="btn-icon">⬇️</span>
          </button>
          <button 
            className="control-btn pause-btn" 
            onClick={() => setPaused(p => !p)}
            disabled={gameOver}
            title={paused ? "继续" : "暂停"}
          >
            <span className="btn-icon">{paused ? '▶️' : '⏸️'}</span>
          </button>
        </div>
        <div className="control-row">
          <button 
            className="control-btn left-btn" 
            onClick={() => move(-1)}
            disabled={gameOver || paused}
            title="左移"
          >
            <span className="btn-icon">⬅️</span>
          </button>
          <button 
            className="control-btn down-btn" 
            onClick={drop}
            disabled={gameOver || paused}
            title="下落"
          >
            <span className="btn-icon">⬇️</span>
          </button>
          <button 
            className="control-btn right-btn" 
            onClick={() => move(1)}
            disabled={gameOver || paused}
            title="右移"
          >
            <span className="btn-icon">➡️</span>
          </button>
        </div>
        <div className="control-row">
          <div className="tetris-next">
            <div className="next-label">下一个</div>
            {renderNext()}
          </div>
          <button className="restart-btn" onClick={restart}>重新开始</button>
        </div>
      </div>

      <div className="tetris-main">
        <div className="tetris-board" style={{ width: COLS * BLOCK_SIZE, height: ROWS * BLOCK_SIZE }}>
          {renderBoard()}
        </div>
      </div>
      {gameOver && (
        <div className="tetris-overlay">
          <div className="overlay-content">
            <h2>游戏结束</h2>
            <p>最终分数: {score}</p>
            <button onClick={restart}>重新开始</button>
          </div>
        </div>
      )}
      {paused && !gameOver && (
        <div className="tetris-overlay paused">
          <div className="overlay-content">
            <h2>暂停中</h2>
            <p>按P键或点击继续</p>
            <button onClick={() => setPaused(false)}>继续</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tetris; 